/**
 * Module dependencies.
 */

import { CardSelectField } from 'app/components/atoms/forms/fields/card-select/card-select-field';
import React, { useMemo } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { CardSelectFieldFormItem } from '../form-types';
import { identity, set } from 'lodash';
import { useParams } from 'react-router-dom';

/**
 * `FormCardSelectFieldProps` type.
 */

export type FormCardSelectFieldProps = {
  name: string;
  item: CardSelectFieldFormItem;
  form: UseFormReturn<FieldValues, any>;
};

/**
 * `ByHook` component.
 *
 * Loads the option cards from the configured request hook, exactly like the
 * select field does, so category/sub-category cascading behaviour is preserved.
 */

function ByHook(props: FormCardSelectFieldProps) {
  const { form, item } = props;
  const { control, watch } = form;
  const { options, disabled, name, label, emptyLabel } = item;
  const { hookProps, normalize, hook } = options as any;
  const params = useParams();
  const values = watch();
  const hookPropsParams = hookProps?.(values, params) ?? [];
  const { data } = hook?.(...hookPropsParams) ?? { data: options };
  const normalizer = normalize ?? identity;
  const optionValues = useMemo(() => normalizer(data?.results ?? data, values), [data, normalizer, values]);

  set(form, ['cache', 'hooks', name], {
    data,
    optionValues
  });

  return (
    <CardSelectField
      control={control}
      disabled={typeof disabled === 'function' ? disabled(values) : disabled}
      emptyLabel={emptyLabel}
      label={label}
      name={name}
      options={optionValues}
    />
  );
}

/**
 * `ByStatic` component.
 */

function ByStatic(props: FormCardSelectFieldProps) {
  const { form, item } = props;
  const { control } = form;
  const { label, disabled, options, name, emptyLabel } = item;

  return (
    <CardSelectField
      control={control}
      disabled={typeof disabled === 'function' ? disabled({}) : disabled}
      emptyLabel={emptyLabel}
      label={label}
      name={name}
      options={options as any}
    />
  );
}

/**
 * Export `FormCardSelectField` component.
 */

export function FormCardSelectField(props: FormCardSelectFieldProps): JSX.Element {
  if ((props.item.options as any)?.hook) {
    return <ByHook {...props} />;
  }

  return <ByStatic {...props} />;
}
